import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import type { Staff } from './api';
import { DEPARTMENT_KITS } from './departments';
import { marketTools, STAFF_TOOLS } from './tools';

export type CrmTheme = 'dark' | 'light';
const KEY = 'ck.crm.theme';

export function savedTheme(): CrmTheme {
  try { return (localStorage.getItem(KEY) as CrmTheme) || 'dark'; } catch { return 'dark'; }
}

/** The CRM opens in dark mode; the switch in the top bar remembers the choice on this device. */
export function useCrmTheme(): [CrmTheme, (t: CrmTheme) => void] {
  const [theme, set] = useState<CrmTheme>(savedTheme);
  useEffect(() => {
    document.documentElement.dataset.app = 'crm';
    document.documentElement.dataset.theme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0E0E10' : '#F5F6F8');
  }, [theme]);
  return [theme, (t) => { try { localStorage.setItem(KEY, t); } catch { /* ignore */ } set(t); }];
}

interface Hit { label: string; sub: string; go: () => void }
const NAV_WORDS: [string, string][] = [
  ['dashboard', 'Dashboard'], ['traders', 'All clients'], ['book', 'My book & leads'], ['accounts', 'Trading accounts'], ['kyc', 'KYC reviews'], ['duplicates', 'Duplicate checks'],
  ['orders', 'Payments'], ['payouts', 'Payouts'], ['settings', 'Payment methods & routing'], ['woocommerce', 'WooCommerce shop'], ['offers', 'Offers & codes'], ['partners', 'Affiliates & IBs'],
  ['plans', 'Challenges & rules'], ['certificates', 'Certificates'], ['markets', 'Markets'], ['staff', 'Team & departments'], ['interns', 'Interns'], ['reports', 'Reports'],
  ['leaderboard', 'Team leaderboard'], ['activity', 'Team activity'], ['countries', 'Countries & compliance'], ['firm-security', 'Security & maintenance'], ['audit', 'Audit log'],
  ['branding', 'Branding'], ['support', 'Support desk'], ['waitlist', 'Waitlist'], ['dept', 'My department'],
];

/** Type to jump to any page, department or tool you can open. */
export function QuickFind({ staff, allowed, go }: { staff: Staff; allowed: (page: string) => boolean; go: (page: string, param?: string) => void }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const everything = ['owner', 'admin'].includes(staff.role);
  const hits = useMemo<Hit[]>(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    const out: Hit[] = [];
    for (const [id, label] of NAV_WORDS) if (label.toLowerCase().includes(t) && allowed(id)) out.push({ label, sub: 'Page', go: () => go(id) });
    if (everything) for (const d of DEPARTMENT_KITS) if (d.name.toLowerCase().includes(t)) out.push({ label: d.name, sub: 'Department', go: () => go('dept', d.id) });
    for (const tool of STAFF_TOOLS) if ((everything || tool.group === staff.role) && tool.name.toLowerCase().includes(t)) out.push({ label: tool.name, sub: tool.groupName, go: () => go('tool', tool.id) });
    if (staff.role !== 'intern') for (const b of ['prop', 'futures', 'perps', 'predict', 'fx']) for (const tool of marketTools(b)) if (tool.name.toLowerCase().includes(t)) out.push({ label: tool.name, sub: `${tool.groupName} · market desk`, go: () => (tool.page && allowed(tool.page) ? go(tool.page) : go('tool', tool.id)) });
    return out.slice(0, 10);
  }, [q]);
  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); box.current?.querySelector('input')?.focus(); } };
    const out = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('keydown', k); document.addEventListener('mousedown', out);
    return () => { document.removeEventListener('keydown', k); document.removeEventListener('mousedown', out); };
  }, []);
  const pick = (h: Hit) => { h.go(); setQ(''); setOpen(false); };
  return (
    <div className="qf" ref={box}>
      <Search aria-hidden="true" />
      <input type="search" placeholder="Search" aria-label="Search pages and tools" value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setSel((x) => Math.min(hits.length - 1, x + 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setSel((x) => Math.max(0, x - 1)); }
          if (e.key === 'Enter' && hits[sel]) { e.preventDefault(); pick(hits[sel]!); }
          if (e.key === 'Escape') setOpen(false);
        }} />
      <kbd>Ctrl K</kbd>
      {open && q.trim().length >= 2 && (
        <ul className="qf-menu" role="listbox">
          {hits.length === 0 ? <li className="qf-none">Nothing matches “{q}”.</li> : hits.map((h, i) => (
            <li key={`${h.sub}${h.label}`}><button role="option" aria-selected={i === sel} className={i === sel ? 'on' : ''} onMouseEnter={() => setSel(i)} onClick={() => pick(h)}><b>{h.label}</b><small>{h.sub}</small></button></li>
          ))}
        </ul>
      )}
    </div>
  );
}
